import {
  Stack,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
// eslint-disable-next-line max-len
import type { Row } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress/Table/Table.types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import messages from '../messages';
import { DEFAULT_TRACS_VOUCHER_ID } from '../../../constants/hudVoucher';
import { getHUDVoucherBadgeConfig } from '../../../constants/badgeStatus';

export const useRows = (
  vouchers = [],
  { onDownloadMat30, onDownloadHud52670, canManage } = {},
) => {
  return useMemo(() => {
    return vouchers.map((voucher) => {
      const hasTracsMessage =
        voucher.tracsMessage && voucher.tracsMessage.trim() !== '';
      const isError =
        hasTracsMessage &&
        voucher.tracsMessage.toUpperCase().startsWith('ERROR:');

      const getTracsMessageContent = () => {
        if (!hasTracsMessage) {
          return null;
        }
        if (isError && voucher.resolution) {
          return (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ width: '100%', paddingTop: 0.5 }}
            >
              <Typography
                variant="bodyMedium"
                sx={{
                  color: 'error.main',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {voucher.tracsMessage}
              </Typography>
              <Tooltip
                variant="light"
                title={voucher.resolution}
                placement="bottom"
              >
                <AlertInfoIcon
                  sx={{
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontSize: 'medium',
                    marginTop: 0.25,
                  }}
                />
              </Tooltip>
            </Stack>
          );
        }
        return (
          <Typography
            variant="bodyMedium"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {voucher.tracsMessage}
          </Typography>
        );
      };
      const tracsMessageContent = getTracsMessageContent();

      const row: Row = {
        id: voucher.id,
        voucherYearMonth: {
          variant: 'link',
          to: `manage-hud-vouchers/voucher-details/${voucher.id}`,
          value: voucher.voucherYearMonth,
        },
        tracsVoucherId: {
          value:
            voucher.submissionStatus === 'Draft' && !voucher.tracsVoucherId
              ? DEFAULT_TRACS_VOUCHER_ID
              : voucher.tracsVoucherId,
        },
        subsidyUnits: {
          variant: 'number',
          value: voucher.subsidyUnits,
        },
        subsidyType: {
          value: voucher.subsidyType,
        },
        contractNumber: {
          value: voucher.affordableFunding?.contractNumber,
        },
        submissionStatus: {
          value: (() => {
            const badgeConfig = getHUDVoucherBadgeConfig(
              voucher.submissionStatus,
            );
            return (
              <BadgeStatus
                status={badgeConfig.status}
                variant={badgeConfig.variant}
                sx={badgeConfig.sx}
                size="small"
              >
                {voucher.submissionStatus}
              </BadgeStatus>
            );
          })(),
        },
        lastSubmissionDate: {
          variant: 'dateTime',
          value: voucher.lastSubmissionDate,
        },
        hudResponseDate: {
          variant: 'date',
          value: voucher.hudResponseDate,
        },
        totalVoucherAmount: {
          variant: 'currency',
          numberFormatOptions: { minimumFractionDigits: 2 },
          value: voucher.totalVoucherAmount,
        },
        tracsMessage: {
          value: tracsMessageContent,
        },
        actions: {
          variant: 'menu',
          name: 'actionsMenu',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              value: 'downloadHud52670',
              text: <FormattedMessage {...messages.downloadHud52670} />,
              onClick: () => onDownloadHud52670?.(voucher.id),
            },
            {
              value: 'downloadMat30',
              text: <FormattedMessage {...messages.downloadMat30} />,
              disabled: !canManage,
              tooltip: !canManage ? (
                <FormattedMessage {...messages.noPermissionTooltip} />
              ) : undefined,
              onClick: canManage ? () => onDownloadMat30?.(voucher.id) : null,
            },
          ],
        },
      };
      return row;
    });
  }, [vouchers, onDownloadMat30, onDownloadHud52670, canManage]);
};
